import React, { memo, useEffect, useMemo, useRef } from 'react';
import Select, { components, createFilter } from 'react-select';
import { FixedSizeList as List } from 'react-window';

const VIRTUALIZE_THRESHOLD = 200;
const ITEM_HEIGHT = 35;

const filterOption = createFilter({ ignoreAccents: false });

const selectStyles = {
  control: base => ({
    ...base,
    minHeight: '32px',
    fontSize: '0.875rem',
  }),
  valueContainer: base => ({
    ...base,
    padding: '0 6px',
  }),
  indicatorsContainer: base => ({
    ...base,
    height: '32px',
  }),
  menuList: base => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
};

/**
 * Row estável p/ react-window. Renderiza o label direto (evita Option+emotion
 * ficar em branco dentro do FixedSizeList) e reusa innerProps do react-select.
 *
 * Remove onMouseMove/onMouseOver: com ~6k options, cada hover atualizava
 * focusedOption no react-select e recriava todos os children (lag).
 */
const VirtualOptionRow = ({ data, index, style }) => {
  const optionEl = data[index];
  if (!optionEl) return null;

  const {
    data: optionData,
    isDisabled,
    isFocused,
    isSelected,
    innerProps,
  } = optionEl.props;

  const { onMouseMove, onMouseOver, ...safeInnerProps } = innerProps;

  return (
    <div
      {...safeInnerProps}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        boxSizing: 'border-box',
        cursor: isDisabled ? 'not-allowed' : 'default',
        backgroundColor: isSelected
          ? '#2684FF'
          : isFocused
            ? '#DEEBFF'
            : 'transparent',
        color: isDisabled ? '#ccc' : isSelected ? '#fff' : '#333',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      aria-disabled={isDisabled || undefined}
    >
      {optionData?.label ?? ''}
    </div>
  );
};

const VirtualizedMenuList = props => {
  const { children, maxHeight, getValue } = props;
  const listRef = useRef(null);

  const shouldVirtualize =
    Array.isArray(children) && children.length > VIRTUALIZE_THRESHOLD;

  const [value] = getValue();
  const selectedIndex =
    shouldVirtualize && value
      ? children.findIndex(child => child?.props?.isSelected)
      : -1;
  const focusedIndex = shouldVirtualize
    ? children.findIndex(child => child?.props?.isFocused)
    : -1;

  // initialScrollOffset com item já selecionado deixava a lista em branco;
  // scrollToItem após mount é estável. Também acompanha foco via teclado.
  useEffect(() => {
    if (!shouldVirtualize) return undefined;
    const index = focusedIndex >= 0 ? focusedIndex : selectedIndex;
    if (index < 0) return undefined;
    const frameId = requestAnimationFrame(() => {
      listRef.current?.scrollToItem(
        index,
        focusedIndex >= 0 ? 'auto' : 'center'
      );
    });
    return () => cancelAnimationFrame(frameId);
  }, [shouldVirtualize, selectedIndex, focusedIndex]);

  if (!shouldVirtualize) {
    return <components.MenuList {...props} />;
  }

  const itemCount = children.length;
  const height = Math.min(maxHeight || 300, itemCount * ITEM_HEIGHT);

  return (
    <List
      ref={listRef}
      height={height}
      itemCount={itemCount}
      itemSize={ITEM_HEIGHT}
      itemData={children}
      width="100%"
      overscanCount={5}
      style={{ overflowX: 'hidden' }}
    >
      {VirtualOptionRow}
    </List>
  );
};

const SelectComponent = ({
  name,
  codValue,
  setFatura,
  options,
  codField,
  textField,
  placeholder = 'Selecione uma opção',
  isDisabled = false,
}) => {
  const selectedValue = useMemo(
    () =>
      options.find(opt => String(opt.value) === String(codValue)) || null,
    [options, codValue]
  );

  const selectComponents = useMemo(() => {
    if (options.length > VIRTUALIZE_THRESHOLD) {
      return { MenuList: VirtualizedMenuList };
    }
    return undefined;
  }, [options.length]);

  const handleChange = selected => {
    if (isDisabled) return;
    setFatura(prev => ({
      ...prev,
      [textField]: selected?.label || '',
      [codField]: selected?.value || '',
    }));
    document.getElementById(codField)?.focus();
  };

  return (
    <Select
      name={name}
      placeholder={placeholder}
      options={options}
      value={selectedValue}
      onChange={handleChange}
      isDisabled={isDisabled}
      isClearable={!isDisabled}
      className="m-0"
      components={selectComponents}
      styles={selectStyles}
      filterOption={filterOption}
    />
  );
};

export default memo(SelectComponent);
